import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useScreen } from '../../../hooks'
import { getItemsFromApiResponse } from '../../../utils/api'
import { toBuildingAddressSelectItems } from '../../Building'
import { useCloseConfirm } from '../../Form'
import {
  MINIMIZED_MODAL_KEY,
  useMinimizableModal,
  useResizeModal,
} from '../../Modal'
import {
  REQUEST_MINIMIZED_REPLACE_CONFIRMATION,
  RequestWizardForm,
  RequestWizardFormPopupContainer,
  useRequestDraftCloseConfirmation,
  useRequestWizardDraft,
} from '../../Request'
import {
  getIdwellToIdwellBuildings,
  getIdwellToIdwellFlats,
  getIdwellToIdwellWizardChunk,
  getInitIdwellToIdwellWizard,
  getOnDeleteIdwellToIdwellFileDocument,
  getProlongIdwellToIdwellWizard,
  getSendIdwellToIdwellWizardPayloadChunk,
} from '../IdwellToIdwell.api'
import { getIdwellToIdwellFiles } from '../IdwellToIdwell.utils'

import { QA_NAME_MODAL_WIZARD } from './IdwellToIdwellRequestWizardFormModal.constants'

import * as styles from './IdwellToIdwellRequestWizardFormModal.module.css'

function IdwellToIdwellRequestWizardFormModal({
  minimizedModalScope,
  outboundUuid,
  title = 'Modal.WizardNewRequestTitleKey',
  onClose,
  ...restProps
}) {
  const { t } = useTranslation()

  const [modal, setModal] = useState(null)

  const { handleClose, isCloseConfirm, setCloseConfirm } = useCloseConfirm({
    onClose,
  })

  const { isMobileScreenSize } = useScreen()

  const { fullScreen, handleResizeModal } = useResizeModal()

  const { draftId, setDraft } = useRequestWizardDraft()

  const closeConfirmation = useRequestDraftCloseConfirmation(setDraft)

  const { handleMinimize, isMinimized, setIsDirty } = useMinimizableModal({
    closeConfirmation,
    minimizeKey: MINIMIZED_MODAL_KEY.REQUEST_WIZARD_FORM,
    qaName: QA_NAME_MODAL_WIZARD,
    replaceConfirmation: REQUEST_MINIMIZED_REPLACE_CONFIRMATION,
    scope: minimizedModalScope,
    sourceId: draftId,
    title: t(title),
    onClose,
  })

  return (
    <RequestWizardForm
      {...restProps}
      FormContainer={RequestWizardFormPopupContainer}
      cardStageFormFieldsProps={{
        filesOptions: {
          getFiles: getIdwellToIdwellFiles(outboundUuid),
          onDeleteFile: getOnDeleteIdwellToIdwellFileDocument(outboundUuid),
        },
      }}
      containerProps={{
        confirmationProps: {
          description: 'Modal.WizardCloseConfirmationTextKey',
        },
        hidden: isMinimized,
        isCloseConfirm,
        isFullScreen: isMobileScreenSize || fullScreen,
        modal,
        qaName: QA_NAME_MODAL_WIZARD,
        setCloseConfirm,
        title,
        onClose,
        onIsDirtyChange: setIsDirty,
        onMinimize: handleMinimize,
        onResize: isMobileScreenSize ? undefined : handleResizeModal,
      }}
      contentComponentProps={{ isFullScreen: isMobileScreenSize || fullScreen }}
      initStageFormFieldsProps={{
        buildingFieldProps: {
          apiParams: { outboundUuid },
          getData: getIdwellToIdwellBuildings,
          getDataItems: getItemsFromApiResponse,
          toPreparedItems: toBuildingAddressSelectItems,
        },
        flatFieldProps: {
          apiParams: { outboundUuid },
          getData: getIdwellToIdwellFlats,
          getDataItems: getItemsFromApiResponse,
        },
        userFieldProps: {
          controlProps: {
            className: styles.disabledPointerEvents,
            indicatorsProps: {
              ButtonArrow: null,
              ButtonClear: null,
            },
          },
        },
      }}
      setModal={setModal}
      wizardApiProps={{
        getChunk: getIdwellToIdwellWizardChunk(outboundUuid),
        initWizard: getInitIdwellToIdwellWizard(outboundUuid),
        prolong: getProlongIdwellToIdwellWizard(outboundUuid),
        sendPayloadChunk: getSendIdwellToIdwellWizardPayloadChunk(outboundUuid),
      }}
      onCancel={handleClose}
      onCardStageClose={() => {
        setCloseConfirm(true)
      }}
      onClose={onClose}
    />
  )
}

export default IdwellToIdwellRequestWizardFormModal
